import * as React from 'react';
import { Route, RouteComponentProps } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import * as ReactModal from 'react-modal';
import Select from 'react-select';
import { ValueType } from 'react-select/lib/types';
import { EventEmitter } from 'events';

import { LocHelperInstance, localization } from '../shared/localization/localization';
import { AppContextProvider } from '../shared/app-context';
import { LogoDisplayGallery } from './logo-gallery/logo-gallery.component';
import { TronTrackingHandler, ITronLogoEvent } from '../tron-tracking/TronTrackingHandler';
import { LogoEditorContainerContext } from './logo-editor/logo-editor-container.component';
import { ISelectorOption } from '../shared/common-dtos';
import { DebuggerInstance } from '../shared/debugger';
import { LogoUploadController } from './logo-upload/logo-upload.controller';
import { onCloseEventName } from '../shared/constants';

export interface ILogoAppControllerProps extends RouteComponentProps<void> {
}

interface ICustomEventData {
    custom1?: string;
    custom2?: string;
    custom3?: string;
}

interface IState {
    isDialogOpen: boolean;
    isDialogClosable: boolean;
    dialogContent?: any;
    title: string;
    lockPopup: boolean;
    apiKey: string | null;
    userId: string | null;
    userLogo: string | null;

    /**
     * True when all resources (translations, custom styles, ...) are loaded
     * and app can be displayed to user
     */
    appReady: boolean;
    customStylesReady: boolean;
    translationsReady: boolean;

    /**
     * Custom data for TronLogo event
     * this data are added to event data in every call of raiseTronlogoEvent
     */
    customEventData: ICustomEventData;
}

const resourceMaxWait = 5000;

class LogoAppControllerComponent extends React.PureComponent<ILogoAppControllerProps, IState> {
    private tronTrackingHandler: TronTrackingHandler;
    private closeFromPopupHandle = (event: KeyboardEvent): void => {
        /* 27 je ACSII code pro klávesu ESC*/
        if (event.keyCode === 27 && event.key === 'Escape') {
            this.closeFromPopUp();
        }
    }

    public appEvents: EventEmitter = new EventEmitter();

    constructor(props: ILogoAppControllerProps) {
        super(props);
        DebuggerInstance.log('Webapp starting with:');
        DebuggerInstance.log(props);

        const query = new URLSearchParams(this.props.location.search);
        const apiKey = query.get('apikey');
        DebuggerInstance.log(apiKey);
        const userGuid = query.get('user');
        DebuggerInstance.log(userGuid);

        let logo: string | null = null; // optional selected logo
        if (query.has('logo')) {
            logo = query.get('logo');
        }

        const customEventData = {
            custom1: query.get('custom1') || undefined,
            custom2: query.get('custom2') || undefined,
            custom3: query.get('custom3') || undefined,
        };

        this.state = {
            title: 'Tron Logo',
            lockPopup: false,
            isDialogOpen: false,
            isDialogClosable: true,
            apiKey,
            userId: userGuid,
            userLogo: logo,
            appReady: false,
            customStylesReady: false,
            translationsReady: false,
            customEventData
        };

        this.tronTrackingHandler = new TronTrackingHandler();
    }

    /**
     * Setup before component is displied
     */
    public async componentWillMount() {
        // backup to show app with defaults when loading resources fails
        setTimeout(() => { this.setState({ appReady: true }); }, resourceMaxWait)
        // load custom styles
        this.addCustomStyles();
        // add popup clsoe handle
        document.addEventListener('keydown', this.closeFromPopupHandle);
        // setup lang selector with options from server
        await this.initializeLang();
        // mark that lang setup is completed
        this.setState({
            translationsReady: true,
            appReady: this.state.customStylesReady || this.state.appReady
        });
    }

    /**
     * initializes user lang from url query param
     */
    private async initializeLang(): Promise<void> {
        const query = new URLSearchParams(this.props.location.search);
        let lang: string | null = null;
        if (query.has('lang')) {
            lang = query.get('lang');
        }
        await LocHelperInstance.initialize(lang);
    }

    /**
     * Clean up before removing component
     * - remove popup handle from listener
     */
    public componentWillUnmount() {
        document.removeEventListener('keydown', this.closeFromPopupHandle);
    }

    /**
     * custom styles based on subject customization settings
     */
    private addCustomStyles() {
        const doc = document;
        const head = doc.head as HTMLHeadElement;

        const css = doc.createElement('link') as HTMLLinkElement;
        css.onload = () => {
            // styles loaded
            this.setState({
                customStylesReady: true,
                appReady: this.state.translationsReady || this.state.appReady
            });
        }
        css.onerror = () => {
            // styles failed to load
            this.setState({
                customStylesReady: true,
                appReady: this.state.translationsReady || this.state.appReady
            });
        }

        css.type = 'text/css';
        css.rel = 'stylesheet';
        css.href = `/api/${this.state.apiKey}/customization/subject-style.css`

        head.appendChild(css);
    }

    private openDialog(content: any, closable: boolean = true): void {
        this.setState({
            isDialogOpen: true,
            isDialogClosable: closable,
            dialogContent: content
        });
    }

    private closeDialog(): void {
        this.setState({
            isDialogOpen: false,
            isDialogClosable: true,
            dialogContent: undefined
        });
    }

    private async setLocalization(lang: string): Promise<void> {
        await LocHelperInstance.setLang(lang);
        this.tronTrackingHandler.setLang(lang);
    }

    /**
     * Handle closing app by clicking to popup overlay or pressing esc
     */
    private closeFromPopUp() {
        if (!this.state.lockPopup) {
            this.closeApp();
        }
    }

    private closeApp(delay?: number): void {
        DebuggerInstance.log('LogoAppControllerComponent - closeApp');
        this.appEvents.emit(onCloseEventName);
        this.tronTrackingHandler.closeDialog(delay);
    }

    private handleLogoChanged(logoId: string | null) {
        DebuggerInstance.log('handling logo changed');
        const oldLogo = this.state.userLogo;

        this.tronTrackingHandler.setLogo(logoId);
        this.setState({
            userLogo: logoId
        });

        // always raise event - requested behaviour by DF
        // if (oldLogo !== logoId) {
            this.handleRaiseTronLogoEvent({
                type: 'MOTIVECHANGE',
                motiveId: logoId || undefined
            })
        // }
    }  
    
    public handleRaiseTronLogoEvent(data: ITronLogoEvent): void {
        this.tronTrackingHandler.tronLogoEventCallback(Object.assign(data, this.state.customEventData));
    }

    private handleLangChanged(selected: ValueType<ISelectorOption>) {
        DebuggerInstance.log('handleLangChanged');
        DebuggerInstance.log(selected);

        const selectedOption = selected as ISelectorOption;

        if (selected) {
            this.setLocalization(selectedOption.value);
        }
    }

    private getLangSelector(): JSX.Element {
        const currLang = LocHelperInstance.getCurrentLanguage();
        const langs = LocHelperInstance.getLanguages();
        let browserLang = localization.getInterfaceLanguage();
        if (browserLang) {
            browserLang = browserLang.substr(0,2).toLowerCase();
        }
        DebuggerInstance.log(`Browser language: ${browserLang}`);

        const options: ISelectorOption[] = 
        langs
            .sort((x, y) => {
                // first browser lang
                if (x.isO2Code === browserLang) {
                    return -10;
                }
                if (y.isO2Code === browserLang) {
                    return 10;
                }

                // second english
                if (x.isO2Code === 'en') {
                    return -9;
                }
                if (y.isO2Code === 'en') {
                    return 9;
                }

                // rest alphabetically
                return x.nativeName.localeCompare(y.nativeName);
            })
            .map((lang) => ({ value: lang.isO2Code, label: lang.nativeName }));
        const selected = options.find(o => o.value === currLang);

        return (
            <Select
                className="lang-selector"
                classNamePrefix="lang-sel"
                isSearchable={false}
                value={selected}
                onChange={(e: ValueType<ISelectorOption>) => this.handleLangChanged(e)}
                options={options} />
        );
    }

    public render() {
        if (!this.state.appReady) {
            // app not ready to be displayed - resouce loading
            return (<div />);
        }

        const langSelector = this.getLangSelector();

        return (
            <AppContextProvider
                value={{
                    closeApp: (delay) => this.closeApp(delay),
                    openDialog: (c, closable) => this.openDialog(c, closable),
                    closeDialog: () => this.closeDialog(),
                    getApiKey: () => this.state.apiKey,
                    getUserId: () => this.state.userId,
                    getLogoId: () => this.state.userLogo,
                    onLogochanged: (id) => this.handleLogoChanged(id),
                    setTitle: title => this.setState({ title }),
                    setPopupLock: lock => this.setState({ lockPopup: lock }),
                    raiseTronlogoEvent: (data) => this.handleRaiseTronLogoEvent(data),
                    appEvents: this.appEvents
                }}
            >
                <ReactModal
                    isOpen={this.state.isDialogOpen}
                    shouldCloseOnEsc={true}
                    shouldCloseOnOverlayClick={true}
                    onRequestClose={() => {if (this.state.isDialogClosable) { this.closeDialog()}}}
                    overlayClassName="modal-overlay"
                    className="modal-content"
                >
                    {this.state.dialogContent}
                </ReactModal>

                <div className='outer-div' onClick={() => { this.closeFromPopUp() }}>
                    <div className='mid-div'>
                        <div className="center-div">
                            <div className="app-root container primary-style"
                                onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                                    event.stopPropagation();
                                    event.preventDefault();
                                }}>
                                <div className="app-title primary-style">
                                    <div className="app-title-text">
                                        <h4>{this.state.title}</h4>
                                    </div>
                                    <div className="localization-container">
                                        {langSelector}
                                    </div>
                                    <div className="app-close">
                                        <button onClick={() => this.closeApp()}>
                                            <FontAwesomeIcon icon={faTimes} color={'black'} />
                                        </button>
                                    </div>
                                </div>
                                <div className="app-content">
                                    <div>
                                        <Route
                                            path={`${this.props.match.url}/upload`}
                                            component={LogoUploadController}
                                        />
                                        <Route
                                            path={`${this.props.match.url}/gallery`}
                                            component={LogoDisplayGallery}
                                        />
                                        <Route
                                            path={`${this.props.match.url}/editor`}
                                            component={LogoEditorContainerContext}
                                        />
                                        <Route
                                            path={`${this.props.match.url}/upload-default-logo`}
                                            component={LogoUploadController}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="app-footer container">
                                <span className="footer-copyright">
                                    Powered by <u><a onClick={(e) => e.stopPropagation()} target="_blank" href="https://www.promotron.com/tronlogo">TronLogo</a></u> 
                                    &nbsp;&#x24B8;&nbsp;
                                    <u><a onClick={(e) => e.stopPropagation()} target="_blank" href="https://www.promotron.com/">PromoTron.com</a></u>
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </AppContextProvider>
        );
    }
  
}

export const LogoAppController = (props: ILogoAppControllerProps) => <LogoAppControllerComponent {...props} />



// WEBPACK FOOTER //
// ./src/App/logo-app/logo-app.controller.tsx